<template>
  <v-app-bar
    :height="hasSubheaders ? '136px' : '85px'"
    app
    class="pa-0 ma-0"
    color="white"
    :elevation="0"
  >
    <v-col
      cols="12"
      :class="{ 'py-9': hasSubheaders, 'px-4': true }"
      style="height: 100%"
    >
      <notification-banner></notification-banner>
      <v-row class="pa-0 ma-0 align-center">
        <v-btn
          class="mr-4"
          fab
          small
          elevation="0"
          color="primary"
          v-if="showBack"
          @click="$router.go(-1)"
        >
          <v-icon size="40">mdi-chevron-left</v-icon>
        </v-btn>
        <v-skeleton-loader
          type="heading"
          min-width="400px"
          v-if="!title"
        ></v-skeleton-loader>
        <h2 class="page-bar-title" v-else>
          <div v-if="title instanceof Array">
            <span v-for="(crumb, idx) in title" :key="crumb + idx">
              <router-link :to="{ name: crumb.to }" v-if="crumb.to"
                >{{ crumb.text }} <span class="shape-oval"></span>
              </router-link>
              <span v-else>
                {{ crumb.text }}
              </span>
            </span>
          </div>
          <div v-else>{{ title }}</div>
        </h2>

        <v-spacer></v-spacer>
        <v-row class="justify-end align-center">
          <v-menu offset-y transition="slide-y-transition" v-if="!isMobile">
            <template v-slot:activator="{ on, attrs }">
              <div class="mx-2" v-on="on" v-bind="attrs">
                <span class="text-body-dark">{{ currentCompanyName }}</span>
                <v-icon>{{
                  attrs['aria-expanded'] === 'false'
                    ? 'mdi-menu-down'
                    : 'mdi-menu-up'
                }}</v-icon>
              </div>
            </template>
            <v-list>
              <v-list-item>
                <v-list-item-title>{{ currentCompanyName }}</v-list-item-title>
              </v-list-item>
              <v-divider v-if="userOtherCompanies.length > 0"></v-divider>
              <template v-for="item in userOtherCompanies">
                <v-list-item :key="item.id" @click="selectCompany(item)">{{
                  item.name
                }}</v-list-item>
              </template>
            </v-list>
          </v-menu>
          <v-menu left bottom offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on">
                <v-avatar color="primary">
                  <span class="white--text headline">{{ initials }}</span>
                </v-avatar>
              </v-btn>
            </template>

            <v-list>
              <v-list-item
                :to="{
                  name: 'profile-personal',
                  params: { id: currentUser.uid },
                }"
              >
                <v-list-item-icon>
                  <v-icon>mdi-account</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Profile</v-list-item-title>
              </v-list-item>
              <v-list-item :to="{ name: 'settings' }">
                <v-list-item-icon>
                  <v-icon>mdi-cog</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Settings</v-list-item-title>
              </v-list-item>
              <v-divider></v-divider>
              <v-list-item @click="switchCompany()">
                <v-list-item-icon>
                  <v-icon>mdi-swap-horizontal</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Switch Company</v-list-item-title>
              </v-list-item>
              <v-list-item @click="logout()">
                <v-list-item-icon>
                  <v-icon>mdi-logout</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Logout</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-row>
      </v-row>
      <v-row class="pa-0 ma-0 align-center" v-if="hasSubheaders">
        <v-spacer></v-spacer>
        <slot name="subactionView"></slot>
      </v-row>
    </v-col>
  </v-app-bar>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { auth } from '@/firebaseConfig'
import { getInitials } from '@/common'
import NotificationBanner from '@/components/NotificationBanner.vue'

export default {
  name: 'PageBar',
  props: {
    title: {
      type: [String, Array],
    },
    showBack: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    NotificationBanner,
  },
  computed: {
    ...mapGetters([
      'currentUser',
      'isMobile',
      'userCompanies',
      'userCompaniesLoading',
      'currentCompany',
    ]),
    currentCompanyName() {
      return this.currentCompany?.name ?? ''
    },
    userOtherCompanies() {
      if (!this.userCompanies || !this.currentCompany) {
        return []
      }

      return this.userCompanies.filter((e) => e.id !== this.currentCompany.id)
    },
    initials() {
      if (this.currentUser.displayName) {
        return getInitials(this.currentUser.displayName)
      } else {
        return getInitials(this.currentUser.email, '', '.')
      }
    },
    username() {
      if (this.currentUser.displayName != null) {
        return this.currentUser.displayName
      } else {
        return this.currentUser.email
      }
    },
    hasSubheaders() {
      return !!this.$scopedSlots['subactionView']
    },
  },
  methods: {
    ...mapActions(['setAppLoading']),
    selectCompany(company) {
      this.setAppLoading(true)
      this.$store.commit('setCurrentCompany', company)
      setTimeout(() => {
        this.setAppLoading(false)
      }, 300)
    },
    switchCompany() {
      this.$router.push({ name: 'launchpad' })
    },
    logout() {
      auth
        .signOut()
        .then(() => {
          this.$store.dispatch('clearState')
          this.$router.push({ name: 'login' })
        })
        .catch((err) => {
          alert(err)
        })
    },
  },
}
</script>

<style lang="sass" scoped>
h1
  font-size: 32px

.page-bar-title
  color: #000

.page-bar-sec1
  display: flex
  flex-direction: row
  flex-grow: 1
  align-items: center

.page-bar-sec2
  display: flex
  flex-direction: row
  flex-grow: 1
  align-items: center

a
  color: $primary
  text-decoration: none
</style>
